import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Pointer origin for the theme swap. Kept in a module-scope ref so a
// pointerdown a second before the toggle click can be remembered
// across renders without state churn.
const themeOrigin: { current: { x: number; y: number } | null } = { current: null };

/** Ref that flips to true after first render so the mount effect can
 *  skip the swap animation on the initial paint. */
function useMountedRef() {
  const ref = useRef(false);
  useEffect(() => {
    ref.current = true;
  }, []);
  return ref;
}

type ThemeMode = "light" | "dark" | "system";

interface ThemeContextValue {
  mode: ThemeMode;
  /** The resolved theme actually painted right now. */
  resolved: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "travelix:theme";

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function applyDomTheme(mode: ThemeMode) {
  const root = document.documentElement;
  if (mode === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", mode);
  }
}

/**
 * When available, run the theme swap through the View Transitions API
 * so the new palette wipes in as a circular reveal from the pointer's
 * last known position. Reduced-motion users skip the animation
 * entirely — VT respects prefers-reduced-motion at the CSS level.
 *
 * Origin is set on <html> via the --tv-origin-* CSS vars so app.css
 * can position the ::view-transition-new(root) clip-path.
 */
function applyDomThemeAnimated(mode: ThemeMode, origin: { x: number; y: number } | null) {
  const doc = document as unknown as {
    startViewTransition?: (cb: () => void) => void;
  };
  const root = document.documentElement;
  if (origin) {
    root.style.setProperty("--tv-theme-origin-x", `${origin.x}px`);
    root.style.setProperty("--tv-theme-origin-y", `${origin.y}px`);
  }
  root.classList.add("tv-theme-swap");
  const finish = () => root.classList.remove("tv-theme-swap");
  if (typeof doc.startViewTransition === "function") {
    doc.startViewTransition(() => applyDomTheme(mode));
    // Best-effort cleanup; the class is only used to key CSS, not to
    // gate JS, so leaving it a moment longer is harmless.
    setTimeout(finish, 600);
  } else {
    applyDomTheme(mode);
    finish();
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [systemIsDark, setSystemIsDark] = useState<boolean>(() =>
    systemPrefersDark(),
  );

  // Track pointer position so the theme swap can radiate from wherever
  // the user last clicked/tapped. Falls back to the top-right corner
  // (where the toggle usually lives) when the pointer is unknown.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const remember = (e: PointerEvent) => {
      themeOrigin.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("pointerdown", remember, { capture: true });
    return () => window.removeEventListener("pointerdown", remember, { capture: true } as EventListenerOptions);
  }, []);

  // Sync CSS state on mount + whenever mode changes. Mount uses the
  // plain path (no animation); subsequent changes go through the
  // circular reveal.
  const mountedRef = useMountedRef();
  useEffect(() => {
    if (!mountedRef.current) {
      applyDomTheme(mode);
    } else {
      applyDomThemeAnimated(mode, themeOrigin.current);
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* storage disabled — non-fatal */
    }
    // Broadcast so native shells (Capacitor status bar) can react.
    window.dispatchEvent(new CustomEvent("travelix:theme-changed", { detail: { mode } }));
  }, [mode, mountedRef]);

  // Watch system preference so "system" mode reacts live.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);

  const toggle = useCallback(() => {
    setModeState((current) => {
      // If following system, flip to the opposite of whatever the system currently paints.
      if (current === "system") return systemIsDark ? "light" : "dark";
      return current === "dark" ? "light" : "dark";
    });
  }, [systemIsDark]);

  const resolved: "light" | "dark" = useMemo(() => {
    if (mode === "system") return systemIsDark ? "dark" : "light";
    return mode;
  }, [mode, systemIsDark]);

  const value: ThemeContextValue = useMemo(
    () => ({ mode, resolved, setMode, toggle }),
    [mode, resolved, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
